/**
 * UpCart Bundle Transformer
 * Transforms "fake" bundles (separate line items with matching __bundle_id)
 * into a single bundle item with components for display in UpCart drawer
 */

// Store mapping of bundle IDs to their component keys
window._bundleComponentKeys = {};

window.upcartModifyCart = function(cart) {
  if (!cart || !cart.items || !Array.isArray(cart.items)) {
    return cart;
  }

  // Group items by __bundle_id
  const bundleGroups = {};
  const nonBundleItems = [];

  cart.items.forEach(item => {
    const bundleId = item.properties?.__bundle_id;

    if (bundleId) {
      if (!bundleGroups[bundleId]) {
        bundleGroups[bundleId] = [];
      }
      bundleGroups[bundleId].push(item);
    } else {
      // Keep non-bundle items as-is (including real bundles with has_components: true)
      nonBundleItems.push(item);
    }
  });

  // Transform fake bundles into bundle format
  const transformedBundles = Object.entries(bundleGroups).map(([bundleId, items]) => {
    const firstItem = items[0];
    const bundleTitle = firstItem.properties.__bundle_product_title;
    const bundleImage = firstItem.properties.__bundle_product_image;

    // Store the component keys for this bundle so we can delete them later
    window._bundleComponentKeys[bundleId] = items.map(item => item.key);

    // Check if all items have the same selling plan
    let bundleSellingPlan = null;
    let sellingPlanMatches = true;

    if (firstItem.selling_plan_allocation) {
      const firstPlanName = firstItem.selling_plan_allocation.selling_plan?.name;

      // Check if all items have matching selling plan names
      sellingPlanMatches = items.every(item =>
        item.selling_plan_allocation?.selling_plan?.name === firstPlanName
      );

      if (sellingPlanMatches && firstPlanName) {
        // Remove [BUNDLE] prefix from the plan name
        const cleanPlanName = firstPlanName.replace('[BUNDLE] ', '');

        bundleSellingPlan = {
          ...firstItem.selling_plan_allocation,
          selling_plan: {
            ...firstItem.selling_plan_allocation.selling_plan,
            name: cleanPlanName
          }
        };
      }
    }

    // Calculate total price and weight for the bundle
    const totalPrice = items.reduce((sum, item) => sum + item.line_price, 0);
    const totalWeight = items.reduce((sum, item) => sum + (item.grams * item.quantity), 0);

    // Calculate total compare_at_price and discount
    let totalCompareAtPrice = 0;
    let bundleCompareAtPrice = null;

    if (bundleSellingPlan) {
      // Sum up all compare_at prices from selling plan allocations
      totalCompareAtPrice = items.reduce((sum, item) => {
        const compareAt = item.selling_plan_allocation?.compare_at_price || 0;
        return sum + (compareAt * item.quantity);
      }, 0);

      bundleCompareAtPrice = totalCompareAtPrice;

      // Update bundle selling plan with total prices
      bundleSellingPlan.price = totalPrice;
      bundleSellingPlan.compare_at_price = totalCompareAtPrice;
      bundleSellingPlan.per_delivery_price = totalPrice;
    }

    // Create item_components array from the bundle items
    const item_components = items.map(item => ({
      quantity: item.quantity,
      variantTitle: item.variant_title,
      product: {
        title: item.product_title,
        has_only_default_variant: item.product_has_only_default_variant
      },
      image: {
        url: item.image,
        alt: item.featured_image?.alt || `Image for ${item.product_title}`,
        height: item.featured_image?.height,
        width: item.featured_image?.width
      }
    }));

    // Create the bundle item
    const bundleItem = {
      // Use first item's ID with a bundle prefix to make it unique
      id: `bundle-${bundleId}`,
      properties: {
        ...firstItem.properties
      },
      quantity: 1,
      variant_id: firstItem.variant_id,
      key: `bundle-${bundleId}`,
      title: bundleTitle,
      price: totalPrice,
      original_price: totalPrice,
      presentment_price: totalPrice / 100,
      discounted_price: totalPrice,
      line_price: totalPrice,
      original_line_price: totalPrice,
      total_discount: 0,
      discounts: [],
      sku: bundleId,
      grams: totalWeight,
      vendor: firstItem.vendor,
      taxable: firstItem.taxable,
      product_id: `bundle-${bundleId}`,
      product_has_only_default_variant: false,
      gift_card: false,
      final_price: totalPrice,
      final_line_price: totalPrice,
      url: firstItem.url,
      featured_image: {
        aspect_ratio: 1,
        alt: bundleTitle,
        height: 220,
        url: bundleImage.startsWith('//') ? `https:${bundleImage}` : bundleImage,
        width: 220
      },
      image: bundleImage.startsWith('//') ? `https:${bundleImage}` : bundleImage,
      handle: bundleId,
      requires_shipping: true,
      product_type: '',
      product_title: bundleTitle,
      product_description: '',
      variant_title: '',
      variant_options: [],
      options_with_values: [],
      line_level_discount_allocations: [],
      line_level_total_discount: 0,
      quantity_rule: {
        min: 1,
        max: null,
        increment: 1
      },
      has_components: true,
      item_components: item_components,
      compare_at_price: bundleCompareAtPrice
    };

    // Add selling_plan_allocation if available
    if (bundleSellingPlan) {
      bundleItem.selling_plan_allocation = bundleSellingPlan;
    }

    return bundleItem;
  });

  // Reconstruct the cart with transformed bundles and non-bundle items
  const modifiedCart = {
    ...cart,
    items: [...nonBundleItems, ...transformedBundles],
    item_count: nonBundleItems.length + transformedBundles.length
  };

  return modifiedCart;
};

/**
 * Handle bundle delete attempts by removing all component items
 */
(function setupBundleDeleteHandler() {
  // Wait for UpCart subscription API to be ready
  const checkUpCart = setInterval(() => {
    if (typeof window.upcartSubscribeItemRemoved !== 'undefined') {
      clearInterval(checkUpCart);
      initBundleDeleteSubscription();
    }
  }, 100);

  function initBundleDeleteSubscription() {
    // Subscribe to item removal events
    window.upcartSubscribeItemRemoved(({ item }) => {
      // Check if this is a bundle item by looking at the key
      if (!item.key || !item.key.startsWith('bundle-')) {
        // Not a bundle, let UpCart handle it normally
        return;
      }

      const bundleId = item.key.replace('bundle-', '');
      const componentKeys = window._bundleComponentKeys[bundleId];

      if (!componentKeys || !componentKeys.length) {
        return;
      }

      // Remove all components of the bundle
      const updates = {};
      componentKeys.forEach(key => {
        updates[key] = 0;
      });

      // Update cart to remove all components
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      })
      .then(response => response.json())
      .then(() => {
        // Trigger UpCart refresh to update the display
        if (typeof window.upcartRefreshCart === 'function') {
          window.upcartRefreshCart();
        }
      })
      .catch(error => {
        console.error('Error removing bundle components:', error);
      });
    });
  }
})();
